import type { KeyboardEvent } from 'react';
import type { Kpi, PeriodColKey } from '../lib/compute';

interface KpiCardProps {
  kpi: Kpi;
  /** Larger value type (Werbung / Produkte cards). */
  large?: boolean;
  /** Cockpit cards append the literal "vs. Vorp." after the delta. */
  vsPrev?: boolean;
  /** When set, the card is a button that selects this metric for the chart. */
  onClick?: () => void;
  /** Highlight as the active metric. */
  selected?: boolean;
}

export function KpiCard({ kpi, large, vsPrev, onClick, selected }: KpiCardProps) {
  const cls = `cw-kpi${large ? ' cw-kpi-lg' : ''}${onClick ? ' cw-clickable' : ''}${selected ? ' cw-selected' : ''}`;
  return (
    <div
      className={cls}
      {...(onClick
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-pressed': selected,
            'aria-label': `${kpi.label} als Diagramm-Metrik wählen`,
            onClick,
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            },
          }
        : {})}
    >
      <div className="cw-kpi-label">{kpi.label}</div>
      <div className="cw-kpi-value cw-num">{kpi.value}</div>
      <div className="cw-kpi-delta">
        {kpi.delta && <span className={`cw-delta cw-num ${kpi.tone}`}>{kpi.delta}</span>}
        {vsPrev ? (
          <span className="cw-vsprev">vs. Vorp.</span>
        ) : (
          kpi.subnote && <span className="cw-vsprev">{kpi.subnote}</span>
        )}
      </div>
    </div>
  );
}

interface PeriodTableProps {
  periodHead: string;
  /** Columns to show (follows the selected Cockpit metric). */
  cols: { label: string; key: PeriodColKey }[];
  rows: ({ label: string } & Record<PeriodColKey, string>)[];
}

const cellClass = (key: PeriodColKey) => (key === 'roas' ? 'r roas' : key === 'spend' ? 'r muted' : 'r');

export function PeriodTable({ periodHead, cols, rows }: PeriodTableProps) {
  return (
    <div className="cw-table-scroll">
      <table className="cw-table">
        <thead>
          <tr>
            <th className="l">{periodHead}</th>
            {cols.map((c) => (
              <th key={c.key} className="r">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => (
            <tr key={w.label}>
              <td className="l kw">{w.label}</td>
              {cols.map((c) => (
                <td key={c.key} className={cellClass(c.key)}>{w[c.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
